/**
 * Widoczność elementów dashboardu per firma + rola.
 *
 * Lista steruje prezentacją kart. Backend dodatkowo wylicza techniczny dostęp
 * `dashboard` z wyboru roli, bez podnoszenia grupy analytics ani modułu stats.
 */

export interface DashboardWidgetDef {
  key: string;
  label: string;
  description: string;
  group: string;
  icon: string;
}

export const DASHBOARD_WIDGET_DEFS: DashboardWidgetDef[] = [
  { key: 'overdue_shipments', label: 'Zaległe wysyłki', description: 'Pasek i lista przesyłek bez potwierdzonej wysyłki.', group: 'Operacje', icon: 'truck' },
  // S-25 (25.09): klucz MUSI być też w core/dashboard_widgets.DASHBOARD_WIDGETS (test zgodności list).
  { key: 'hanging_labels', label: 'Wiszące etykiety', description: 'Etykiety, które powstały, a paczka nie wyjechała — w tym opłacone i nieanulowane u przewoźnika.', group: 'Operacje', icon: 'package-search' },
  { key: 'ended_offers', label: 'Zakończone oferty', description: 'Oferty marketplace zakończone lub wymagające uwagi.', group: 'Operacje', icon: 'archive-x' },
  { key: 'kpi_orders_today', label: 'Zamówienia dziś', description: 'Liczba dzisiejszych zamówień wraz z porównaniem.', group: 'KPI', icon: 'shopping-cart' },
  { key: 'kpi_revenue_today', label: 'Przychód dziś', description: 'Dzisiejszy przychód i rozbicie na źródła.', group: 'KPI', icon: 'trending-up' },
  { key: 'kpi_new_customers', label: 'Nowi klienci', description: 'Klienci składający pierwsze zamówienie.', group: 'KPI', icon: 'users' },
  { key: 'kpi_average_value', label: 'Średnia wartość', description: 'Średnia wartość dzisiejszego zamówienia.', group: 'KPI', icon: 'bar-chart-3' },
  { key: 'kpi_month_total', label: 'Cały miesiąc', description: 'Przychód i liczba zamówień w bieżącym miesiącu.', group: 'KPI', icon: 'calendar' },
  { key: 'kpi_attention_needed', label: 'Wymaga uwagi', description: 'Wiadomości i sprawy oczekujące na reakcję.', group: 'KPI', icon: 'alert-triangle' },
  { key: 'kpi_top_products', label: 'TOP 10 produktów', description: 'Najczęściej sprzedawane produkty z ostatnich 7 dni.', group: 'KPI', icon: 'star' },
  { key: 'kpi_sales_marketplace', label: 'Sprzedaż per marketplace', description: 'Udział marketplace’ów w dzisiejszej sprzedaży.', group: 'KPI', icon: 'activity' },
  { key: 'sales_chart', label: 'Wykres zamówień i przychodu', description: 'Wykres 30 dni, 12 miesięcy lub 5 lat.', group: 'Wykresy i listy', icon: 'bar-chart-3' },
  { key: 'offer_growth', label: 'Nowo dodane oferty', description: 'Wykres nowych ofert z rozbiciem na marketplace i konta.', group: 'Wykresy i listy', icon: 'trending-up' },
  { key: 'recent_orders', label: 'Ostatnie zamówienia', description: 'Tabela pięciu najnowszych zamówień.', group: 'Wykresy i listy', icon: 'list-ordered' },
  { key: 'system_updates', label: 'Aktualizacje systemu', description: 'Lista ostatnich zmian i nowych funkcji SellBase.', group: 'Pozostałe', icon: 'megaphone' },
  { key: 'onboarding', label: 'Wprowadzenie dla nowych użytkowników', description: 'Warunkowa checklista pierwszej konfiguracji firmy.', group: 'Pozostałe', icon: 'rocket' },
];

const ALL_KEYS = new Set(DASHBOARD_WIDGET_DEFS.map((widget) => widget.key));
const KPI_KEYS = new Set(DASHBOARD_WIDGET_DEFS.filter((widget) => widget.group === 'KPI').map((widget) => widget.key));

// STAN POCZĄTKOWY: PUSTY, czyli „jeszcze nie wiemy".
//
// ZGŁOSZENIE USERA 2026-08-09: na koncie operatora „historia zamówień, która nie
// powinna się wyświetlać dla danej roli, przez ułamek sekundy jednak jest widoczna
// i znika". Powód: startowało tu `new Set(ALL_KEYS)`, czyli DOMYŚLNIE WSZYSTKIE
// widgety widoczne, a lista dla roli tylko je ZAWĘŻAŁA po powrocie
// `/api/dashboard/widgets`. Ten sam wzorzec „w otwartą stronę", który wcześniej
// znaleźliśmy w menu — tyle że piętro niżej, na treści.
//
// Rozróżniamy TRZY stany, bo „nie wiem" i „wiem, że nic" wyglądają na ekranie inaczej:
//   • niezaładowane        → nie renderujemy ani widgetów, ani komunikatu „brak elementów"
//   • załadowane, pusty    → pokazujemy „Brak elementów Dashboardu dla tej roli"
//   • załadowane, z listą  → pokazujemy dokładnie to, co wolno
let visibleKeys = new Set<string>();
let zaladowano = false;
// 18-D1: trzeci stan — „nie udało się wczytać układu" (pierwsze ładowanie padło). Wcześniej pulpit był
// wtedy pusty bez słowa i bez przycisku ponowienia, aż do ponownego wejścia na trasę.
let ladowanieNieudane = false;

export function dashboardWidgetsLoadFailed(): boolean {
  return ladowanieNieudane;
}

export function setDashboardWidgetVisibility(keys: unknown): void {
  if (!Array.isArray(keys)) {
    // Odpowiedź w nieoczekiwanym kształcie traktujemy jak brak wiedzy, NIE jak
    // pozwolenie na wszystko. Wcześniej było odwrotnie i to samo w sobie odsłaniało
    // komplet widgetów przy byle zmianie kontraktu API.
    visibleKeys = new Set<string>();
    zaladowano = false;
    return;
  }
  visibleKeys = new Set(keys.map(String).filter((key) => ALL_KEYS.has(key)));
  zaladowano = true;
  ladowanieNieudane = false;
}

export function dashboardWidgetsZaladowane(): boolean {
  return zaladowano;
}

// P8 (2026-09-15, PRZ-U8): `/api/dashboard/widgets` leciało dwa razy przy wejściu —
// `handleRouteChange` przy starcie potrafi wołać `renderDashboard` dwukrotnie (bootstrap
// `.finally` + hydratacja), a każde wołanie robiło własny fetch. Teraz obietnica W LOCIE
// jest współdzielona, a świeży wynik (≤ 30 s) nie jest pobierany ponownie. Zapis widoku roli
// w Ustawieniach (settings-tabs.ts, bez przeładowania strony) woła
// `window.resetDashboardWidgetVisibilityCache()` — powrót na Pulpit od razu bierze nowy zestaw.
export const WIDGETS_FRESH_MS = 30_000;
let _wLocie: Promise<void> | null = null;
let _swiezeOd = 0;

/** Testy: wymuś ponowne pobranie (zapomnij o świeżości i obietnicy w locie). */
export function resetDashboardWidgetVisibilityCache(): void {
  _wLocie = null;
  _swiezeOd = 0;
}

export function loadDashboardWidgetVisibility(): Promise<void> {
  if (_wLocie) return _wLocie;
  if (zaladowano && _swiezeOd && (Date.now() - _swiezeOd) < WIDGETS_FRESH_MS) return Promise.resolve();
  _wLocie = _loadDashboardWidgetVisibility().finally(() => { _wLocie = null; });
  return _wLocie;
}

async function _loadDashboardWidgetVisibility(): Promise<void> {
  const api = (window as any).SellBaseAPI;
  if (!api || typeof api.getDashboardWidgets !== 'function') {
    // Brak warstwy API = brak wiedzy o uprawnieniach. Pusty pulpit jest stanem
    // widocznym i diagnozowalnym; pulpit pełen cudzych widgetów nie jest.
    console.warn('[dashboard-widgets] brak SellBaseAPI — pulpit zostaje pusty');
    setDashboardWidgetVisibility(null);
    return;
  }
  try {
    const response = await api.getDashboardWidgets();
    setDashboardWidgetVisibility(response && response.visible_widgets);
    _swiezeOd = Date.now();
  } catch (error) {
    // Chwilowy błąd API nie może wyczyścić dashboardu, który JUŻ raz poprawnie się
    // załadował — ta intencja z poprzedniej wersji zostaje. Zmienia się kierunek
    // przy PIERWSZYM ładowaniu: wtedy nie wiemy jeszcze nic, więc nie pokazujemy nic.
    // Poprzednio obie sytuacje kończyły się „pokaż wszystko".
    if (zaladowano) {
      console.warn('[dashboard-widgets] błąd odświeżenia — zostawiam poprzednią listę:', error);
      return;
    }
    console.warn('[dashboard-widgets] błąd pierwszego ładowania — pulpit pokazuje stan błędu z ponowieniem:', error);
    setDashboardWidgetVisibility(null);
    ladowanieNieudane = true;
  }
}

export function isDashboardWidgetVisible(key: string): boolean {
  return visibleKeys.has(key);
}

export function hasVisibleDashboardKpis(): boolean {
  return Array.from(KPI_KEYS).some((key) => visibleKeys.has(key));
}

/** Ile kafelków KPI ta rola realnie zobaczy. Do rysowania szkieletów ładowania.
 *
 * ZGŁOSZENIE USERA 2026-08-09: „na roli pakowacz jak refreshuję, to jest te 7 kafelków
 * szarych, a ta rola nie ma tych stat wszystkich". Powód: liczba szkieletów była
 * zaszyta na sztywno (`buildSkeletons(7)`), niezależnie od uprawnień. Rola z jednym
 * wskaźnikiem dostawała siedem szarych obietnic, z których sześć znikało.
 *
 * Szkielet ładowania ma zapowiadać to, co ZA CHWILĘ się pojawi. Jeśli zapowiada
 * więcej, to nie jest wskaźnik postępu tylko fałszywa obietnica — i wygląda jak
 * błąd ładowania, choć wszystko działa poprawnie.
 */
export function visibleKpiCount(): number {
  return Array.from(KPI_KEYS).filter((key) => visibleKeys.has(key)).length;
}

export function hasAnyVisibleDashboardWidget(): boolean {
  return visibleKeys.size > 0;
}

export function dashboardKpiWidgetKey(kpi: any): string {
  if (kpi && kpi.widget_key && ALL_KEYS.has(String(kpi.widget_key))) return String(kpi.widget_key);
  const byLabelKey: Record<string, string> = {
    'dashboard.kpi.orders_today': 'kpi_orders_today',
    'dashboard.kpi.revenue_today': 'kpi_revenue_today',
    'dashboard.kpi.new_customers': 'kpi_new_customers',
    'dashboard.kpi.avg_value': 'kpi_average_value',
    'dashboard.kpi.month_total': 'kpi_month_total',
    'dashboard.kpi.attention_needed': 'kpi_attention_needed',
    'dashboard.kpi.top_products_10': 'kpi_top_products',
    'dashboard.kpi.sales_per_marketplace': 'kpi_sales_marketplace',
  };
  return byLabelKey[String(kpi && kpi.label_key || '')] || '';
}

function setElementVisible(element: HTMLElement, visible: boolean): void {
  element.hidden = !visible;
  element.setAttribute('aria-hidden', visible ? 'false' : 'true');
  if (visible) element.style.removeProperty('display');
  else element.style.display = 'none';
}

export function applyDashboardWidgetVisibility(): void {
  document.querySelectorAll<HTMLElement>('#view-dashboard [data-dashboard-widget]').forEach((element) => {
    const key = element.dataset.dashboardWidget || '';
    setElementVisible(element, isDashboardWidgetVisible(key));
  });
  const kpiContainer = document.getElementById('kpi-container');
  if (kpiContainer) {
    setElementVisible(kpiContainer, hasVisibleDashboardKpis() || isDashboardWidgetVisible('onboarding'));
  }
  const emptyState = document.getElementById('dashboard-role-empty');
  // „Brak elementów Dashboardu dla tej roli" pokazujemy TYLKO gdy naprawdę wiemy,
  // że lista jest pusta. Przed załadowaniem stan jest nieznany — komunikat o braku
  // uprawnień byłby wtedy równie mylący jak wcześniejsze pokazywanie wszystkiego,
  // tylko w drugą stronę.
  if (emptyState) {
    setElementVisible(emptyState, zaladowano && !hasAnyVisibleDashboardWidget());
  }
  const loadError = document.getElementById('dashboard-load-error');
  if (loadError) setElementVisible(loadError, !zaladowano && ladowanieNieudane);

  // DOPIERO TERAZ odsłaniamy pulpit — po ustawieniu widoczności KAŻDEJ powłoki.
  // Kolejność jest krytyczna: flaga postawiona wcześniej odsłoniłaby tym samym
  // tickiem karty, których rola widzieć nie powinna, czyli dokładnie ten objaw,
  // który naprawiamy. Flagę stawiamy tylko gdy naprawdę WIEMY (patrz `zaladowano`) —
  // przy nieudanym pierwszym ładowaniu pulpit zostaje pusty zamiast pokazać cudze.
  if (zaladowano) {
    try {
      document.documentElement.setAttribute('data-dashboard-ready', '1');
    } catch (_e) { /* brak DOM */ }
  }
}

export function allDashboardWidgetKeys(): string[] {
  return DASHBOARD_WIDGET_DEFS.map((widget) => widget.key);
}

if (typeof window !== 'undefined') {
  // P8 (recenzja P8-FRONT-7): wołane po zapisie widoku roli w Ustawieniach.
  (window as any).resetDashboardWidgetVisibilityCache = resetDashboardWidgetVisibilityCache;
}
